import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DashLangService } from "../../../../../modules/home/dash/service/dash.lang.service";
import { UserprefService } from "../../../../../services/userpref.service";
import { S25LoadingInlineStaticComponent } from "../../../../../modules/s25-loading/s25.loading.inline.static.component";
import { S25IconComponent } from "../../../../../modules/s25-icon/s25.icon.component";
import { HomeDashCustomize, HomeDashPanelModel } from "../home.dash.panel.model";
import { DashQuickSearchUtil, type DashQuickSearchRow } from "./home.dash.quick.search.util";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { s25Resource } from "../../../../../util/s25.resource";

type QuickSearchContext = {
    style: { panel: string; body: string; item: Record<number, string> };
    text: Record<number, { name: string; button: string }>;
    alert: { too_short_search_term: string; invalid_event_locator: string };
    itemsloading: string;
};

@TypeManagerDecorator("s25-ng-dash-panel-quick-search")
@Component({
    selector: "s25-ng-dash-panel-quick-search",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25LoadingInlineStaticComponent, S25IconComponent],
    host: { class: "s25-ng-dash-panel-quick-search dash-panel-quick-search" },
    template: `
        @if (vm(); as data) {
            @if (showQuickSearchBody()) {
                <div
                    [attr.id]="data.context.style.body"
                    class="sectionBody dash-panel-body dash-panel-body--padded collapse-no-animation"
                >
                    @if (data.model.isLoading) {
                        <s25-ng-loading-inline-static [text]="data.context.itemsloading" />
                    }
                    @if (data.model.items) {
                        <ul class="none-list">
                            @for (row of asRows(data.model.items); track row.itemTypeId) {
                                <li [attr.id]="data.context.style.item[row.itemTypeId]">
                                    @if (rowAlerts()[row.itemTypeId]; as alertText) {
                                        <div class="cn-alert cn-alert--warning c-margin-bottom--quarter" role="alert">
                                            {{ alertText }}
                                        </div>
                                    }
                                    <div class="c-inputGroup">
                                        <input
                                            class="My25Live_QuickSearchInput c-input"
                                            type="text"
                                            [(ngModel)]="row.itemName"
                                            [class.my25live-quick-search-input-filled]="row.isFilled"
                                            (focus)="onRowFocus(row)"
                                            (blur)="onRowBlur(row)"
                                            [placeholder]="rowLabel(data.context, row)"
                                            (keydown.enter)="onSearchRow(row, data.context)"
                                            tabindex="0"
                                            [attr.aria-label]="rowLabel(data.context, row)"
                                        />
                                        <button
                                            class="My25Live_QuickSearch_Button btn btn-default"
                                            type="button"
                                            (click)="onSearchRow(row, data.context)"
                                            tabindex="0"
                                        >
                                            @if (data.context.text[row.itemTypeId].button === "Go") {
                                                <span class="visually-hidden">{{
                                                    data.context.text[row.itemTypeId].button
                                                }}</span>
                                                <s25-ng-icon type="search" [ariaLabel]="false" />
                                            } @else {
                                                {{ data.context.text[row.itemTypeId].button }}
                                            }
                                        </button>
                                    </div>
                                </li>
                            }
                        </ul>
                    }
                </div>
            }
        }
    `,
})
export class HomeDashPanelQuickSearchComponent {
    readonly model = input.required<HomeDashPanelModel>();
    readonly isToolbox = input(false);
    readonly customize = input<HomeDashCustomize | undefined>(undefined);
    /** In sync with {@link HomeDashPanelComponent.listRefreshTick} when panel data mutates in place. */
    readonly refreshTick = input(0);

    /** Lang slice only — do not gate the body on prefs that are only needed at search time. */
    private readonly lang = s25Resource(
        async () => (await DashLangService.getLang()).panel?.["quick_search"] as QuickSearchContext | undefined,
    );
    private readonly tzName = s25Resource(() => UserprefService.getTZName());

    readonly rowAlerts = signal<Record<number, string | undefined>>({});

    readonly showQuickSearchBody = computed(() => {
        this.refreshTick();
        return !this.isToolbox() && !this.model().isCollapsed;
    });

    readonly vm = computed(() => {
        this.refreshTick();
        const context = this.lang.value();
        const model = this.model();
        if (!context) {
            return undefined;
        }
        return { context, model };
    });

    asRows(items: unknown): DashQuickSearchRow[] {
        return items as DashQuickSearchRow[];
    }

    rowLabel(context: QuickSearchContext, row: DashQuickSearchRow) {
        return context.text[row.itemTypeId]?.name ?? "";
    }

    onRowFocus(row: DashQuickSearchRow) {
        DashQuickSearchUtil.focus(row);
    }

    onRowBlur(row: DashQuickSearchRow) {
        DashQuickSearchUtil.blur(row);
    }

    async onSearchRow(row: DashQuickSearchRow, context: QuickSearchContext) {
        const zoneName = this.tzName.value();
        if (!zoneName) {
            return;
        }
        await DashQuickSearchUtil.search(row, context, zoneName, (message) => {
            this.rowAlerts.update((prev) => ({
                ...prev,
                [row.itemTypeId]: message,
            }));
        });
    }
}
